import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { KAL_DIALOG_DATA, KalDialogRef } from '@kalidea/kaligraphi';
import { UntypedFormControl, Validators } from '@angular/forms';
import { CommentScope } from 'src/app/_shared/components/comment-edit/shared-comment-edit.component';

export interface SharedAddCommentDialogData {
    publicComment: string;
    privateComment?: string;
    publicCommentValidators?: Validators[];
    privateCommentValidators?: Validators[];
    disabledPublicComment?: boolean;
    disabledPrivateComment?: boolean;
    publicCommentLabel?: string;
    privateCommentLabel?: string;
    toSnackbarMessage?: () => string;
    scope?: CommentScope;
    readonly isUpdated?: boolean;
}

@Component({
    selector: 'app-shared-add-comment-dialog',
    templateUrl: './shared-add-comment-dialog.component.html',
    styleUrls: ['./shared-add-comment-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAddCommentDialogComponent implements OnInit {

    commentControl: UntypedFormControl;

    commentsResult: SharedCommentsResult;

    constructor(private dialogRef: KalDialogRef<SharedAddCommentDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public data: SharedAddCommentDialogData) {
    }

    submit(): void {
        this.commentsResult.publicComment = this.commentControl.value?.publicComment || '';
        this.commentsResult.privateComment = this.commentControl.value?.privateComment || '';

        this.dialogRef.close(this.commentsResult);
    }

    cancel(): void {
        this.dialogRef.close();
    }

    ngOnInit(): void {
        this.commentsResult = new SharedCommentsResult(this.data?.publicComment, this.data?.privateComment, this.data?.scope);

        this.commentControl = new UntypedFormControl({
            publicComment: this.data.publicComment,
            privateComment: this.data.privateComment
        });
    }
}

export class SharedCommentsResult implements SharedAddCommentDialogData {

    publicComment: string;
    privateComment: string;

    constructor(private previousPublicComment?: string,
                private previousPrivateComment?: string,
                public scope: CommentScope = CommentScope.Both) {
    }

    /**
     * Permet de savoir si une modification a été apportée sur les commentaires
     */
    get isUpdated(): boolean {
        const privateResult = this.getCommentResult('private');
        const publicResult = this.getCommentResult('public');

        return privateResult.added
            || privateResult.updated
            || privateResult.deleted
            || publicResult.added
            || publicResult.updated
            || publicResult.deleted;
    }

    /**
     * Génération du message à afficher dans la Snackbar à la validation du dialog
     */
    toSnackbarMessage(): string {
        const privateResult = this.getCommentResult('private');
        const publicResult = this.getCommentResult('public');

        let message = '';
        const actions = {added: 'ajouté', updated: 'modifié', deleted: 'supprimé'};
        Object.keys(actions).map(a => {
            const actionMessage = this.constructMessage(privateResult[a], publicResult[a], actions[a]);

            if (!!actionMessage) {
                message += !!message ? ' et le' : 'Le';
                message += actionMessage;
            }
        });
        if (!!message) {
            message += '.';
        }
        return `${message}`;
    }

    /**
     * Génération du message pour une action donnée (ajout/modification/suppression)
     * @param privateAction Est-ce que le commentaire privé est concerné par cette action ?
     * @param publicAction Est-ce que le commentaire public est concerné par cette action ?
     * @param action Action concernée
     */
    private constructMessage(privateAction: boolean, publicAction: boolean, action: string): string {
        if (privateAction && publicAction) {
            return `s commentaires ont été ${action}s`;
        }

        if (privateAction || publicAction) {
            const commentScope = privateAction ? 'privé' : 'public';
            return ` commentaire ${this.scope !== CommentScope.Unscoped ? commentScope : ''} a été ${action}`;
        }

        return '';
    }

    /**
     * Retourne un objet indiquant les modifications apportées sur le commentaire
     * @param type privé ou public
     */
    private getCommentResult(type: 'public' | 'private'): { added: boolean, updated: boolean, deleted: boolean } {
        const current = `${type}Comment`;
        const previous = `previous${type.charAt(0).toUpperCase() + type.slice(1)}Comment`;

        return {
            added: (!!this[current] && !this[previous]),
            updated: (!!this[current] && !!this[previous] && this[current] !== this[previous]),
            deleted: (!this[current] && !!this[previous])
        };
    }

}
